import {Component, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {AlertTypesEnum} from '../shared/interfaces/AlertTypesEnum';
import {NotifierService} from 'angular-notifier';
import {NotifierNotificationOptions} from 'angular-notifier/lib/models/notifier-notification.model';
import {BadgeColorsEnum} from '../shared/interfaces/BadgeColorsEnum';
import {Select, Store} from '@ngxs/store';
import {DrugsState} from '../shared/states/drugs/drugs.state';
import {Observable} from 'rxjs/internal/Observable';
import {IDrug} from '../shared/interfaces/drug.interface';
import {GetDrugListFromSearch} from '../shared/states/drugs/drugs.actions';
import {IPharmacy} from '../shared/interfaces/pharmacy.interface';
import {IDrugSearchParams} from '../shared/interfaces/drug-search-params.interface';
import {StrongPasswordValidator} from '../shared/validators/strong-password.validator';
import { UserStatusEnum } from '../shared/interfaces/UserStatusEnum';
import { UserRolesEnum } from '../shared/interfaces/UserRolesEnum';

export enum AuthProgressEnum {
  CreateAccount = 0,
  PharmacyDetails = 1,
  SetPIN = 2,
}

@Component({
  selector: 'app-constructor',
  templateUrl: './constructor.component.html',
  styleUrls: ['./constructor.component.scss'],
})
export class ConstructorComponent implements OnInit {
  exampleForm: UntypedFormGroup;
  isPasswordHidden = true;
  showOpenDialog = false;
  showDialogProgress = false;
  currentDialogProgress = AuthProgressEnum.PharmacyDetails;
  currentStep = AuthProgressEnum.PharmacyDetails;

  dialogProgressLabels: string[] = [
    'Create account',
    'Pharmacy details',
    'Set your PIN',
  ];

  optionsList: IPharmacy[] = [
    {
      id: 'fjkd-gfgf-43m3k4-43mf34k',
      name: 'Periodic Inventory Report',
      address: 'This is the most common report type.',
      status: UserStatusEnum.Active,
      role: UserRolesEnum.PharmacyAdmin
    },
    {
      id: '4903jf9j-439fij-f4j95-4f98',
      name: 'Biennial Inventory Report',
      address: 'This report should be run once every two years.',
      status: UserStatusEnum.Active,
      role: UserRolesEnum.PharmacyAdmin
    },
  ];

  statesList = [
    'All Time',
    'This Week',
    'This Month',
    'This Quarter',
    'This Year',
    'Custom Period',
  ];

  drugsList = [
    {id: 0, name: 'Dexmethylphenidate Hydrochloride 10 mg TAB'},
    {id: 2, name: 'Dexmethylphenidate Hydrochloride 100 mg TAB'},
    {id: 458, name: 'Dexmethylphenidate Hydrochloride 1000 mg TAB'},
  ];

  tableOptions = {
    asc: false,
    rows: [
      {
        firstKey: 'Covid-19',
        leftAligned: '1976',
        center: '9',
        rightAligned: '9',
        lastColumn: 'Huston on the line',
      },
      {
        firstKey: 'Covid-20',
        leftAligned: '1976',
        center: '6',
        rightAligned: '7',
        lastColumn: 'London Calling',
      },
      {
        firstKey: 'Covid-21',
        leftAligned: '1974',
        center: '17',
        rightAligned: '178',
        lastColumn: 'Alaska Bears',
      },
    ],
  };

  BadgeColorsEnum = BadgeColorsEnum;
  AlertTypesEnum = AlertTypesEnum;

  @ViewChild('customNotification', {static: true}) customNotificationTmpl;

  minDate = new Date(new Date().setMonth(new Date().getMonth() - 1));
  maxDate = new Date(new Date().setMonth(new Date().getMonth() + 1));

  private readonly notifier: NotifierService;

  dropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'name',
    selectAllText: 'All Drugs',
    unSelectAllText: 'Deselect All',
    itemsShowLimit: 4,
    allowSearchFilter: true,
    searchPlaceholderText: 'Search Drugs',
  };

  @Select(DrugsState.drugList) drugList$: Observable<IDrug[]>;
  @Select(DrugsState.loading) loading$: Observable<boolean | undefined>;

  constructor(
    private store: Store,
    private notifierService: NotifierService,
    private fb: UntypedFormBuilder,
  ) {
    this.notifier = notifierService;
  }

  ngOnInit(): void {
    this.exampleForm = this.fb.group({
      password: ['', [Validators.required, StrongPasswordValidator]],
      urlLink: ['', [Validators.required]],
      networkLink: ['', [Validators.required]],
      period: ['All Time', [Validators.required]],
      accountNumber: [null, [Validators.required]],
      radioInputValue: [null, [Validators.required]],
      checkAll: [null, [Validators.required]],
      checkCodeine: [null, [Validators.required]],
      switchTheme: [false, []],
      datepicker: ['', []],
      datepickerRange: ['', []],
      searchOptionName: ['', []],
      multiselectDrugs: [[], []],
    });

    this.notifier.show({
      template: this.customNotificationTmpl,
      type: AlertTypesEnum.Success,
      message: 'Successful message',
      id: '1',
    });

    this.notifier.show({
      template: this.customNotificationTmpl,
      type: AlertTypesEnum.Info,
      message: 'Info message',
      id: '2',
    });

    this.notifier.show({
      template: this.customNotificationTmpl,
      type: AlertTypesEnum.Confirm,
      message: 'Confirm message',
      id: '3',
    });

    this.notifier.show({
      template: this.customNotificationTmpl,
      type: AlertTypesEnum.Error,
      message: 'Error message',
      id: '4',
    });

    this.notifier.show({
      hasCloseButton: true,
      template: this.customNotificationTmpl,
      type: AlertTypesEnum.Confirm,
      message: 'Confirm message',
      id: '5',
    } as NotifierNotificationOptions);

    const params: IDrugSearchParams = {
      pharmacyID: null,
      token: '',
      skip: 0,
      active: true,
      limitToPharmacy: true
    };
    this.store.dispatch(new GetDrugListFromSearch(params));
  }

  clickPrimary(): void {
    this.notifier.show({
      template: this.customNotificationTmpl,
      hasCloseButton: true,
      type: AlertTypesEnum.Confirm,
      message: 'Example message',
      id: '2',
    } as NotifierNotificationOptions);
  }

  closePermanently(): void {
    console.log('closePermanently');
  }

  closeThisAlert(id: string): void {
    this.notifier.hide(id);
  }

  chosenOption(ev: any): void {
    console.log(ev);
  }

  drugOptionsChanged(ev: number): void {
    console.log(ev);
  }

  removeBadge(): void {
    console.log('removeBadge');
  }

  openDialog(ev: any): void {
    if (ev.target.checked) {
      this.showOpenDialog = true;
    }
  }

  closeDialog(): void {
    this.exampleForm.get('switchTheme').patchValue(false);
  }

  pinCompleted(pin: string): void {
    console.log(pin);
  }

  toggleUserMenu(popover, data?): void {
    popover.isOpen() ? popover.close() : popover.open(data && {user: data});
  }

  updateDatePicker(ev: any): void {
    this.exampleForm.get('datepicker')?.patchValue(ev.value);
  }

  updateDatePickerRange(ev: any): void {
    this.exampleForm.get('datepickerRange')?.patchValue(ev.value);
  }

  handleSearchInput(token: string): void {
    const params: IDrugSearchParams = {
      pharmacyID: null,
      token,
      skip: 0,
      active: true,
      limitToPharmacy: true
    };
    this.store.dispatch(new GetDrugListFromSearch(params));
  }

  optionSelected(option: IDrug): void {
    console.log(option);
  }

  dropDownClose(): void {
    this.dropdownSettings.itemsShowLimit = 2;
  }

  itemSelect(drug: any): void {
    console.log(drug);
  }

  itemDeSelect(drug: any): void {
    console.log(drug);
  }

  selectAll(all: any): void {
    console.log(all);
  }

  filterDrugsOnSearch(searchValue: string): void {
    console.log(searchValue);
  }

  openDialogProgress(): void {
    this.showDialogProgress = true;
  }

  progressStepSelected(step: number): void {
    this.currentStep = step;
  }

  openWidget(): void { }
}
